import { EventEmitter } from "node:events"
import { createORPCClient } from "@orpc/client"
import { RPCLink } from "@orpc/client/fetch"
import { mapWebLangToServerLang } from "@/lib/i18n/utils"
import {
	getCurrentAreaInClient,
	getCurrentLanguageInClient,
} from "@/lib/utils.client"
import type { RealtimeTransport } from "./transport"
import { ConnectionStatus, type RealtimeEvent } from "./types"

export class SseTransport extends EventEmitter implements RealtimeTransport {
	private orpcClient: any
	private iterator: any = null
	private abortController: AbortController | null = null
	private status: ConnectionStatus = ConnectionStatus.DISCONNECTED
	private isListening = false
	private lastEventTimestamp: number | null = null

	constructor() {
		super()
		this.setupORPCClient()
	}

	/**
	 * Setup oRPC client
	 */
	private setupORPCClient(): void {
		const API_URL = `${process.env.NEXT_PUBLIC_SERVER_URL}/rpc`
		this.orpcClient = createORPCClient(
			new RPCLink({
				url: API_URL,
				headers: () => ({
					"Accept-Language": mapWebLangToServerLang(
						getCurrentLanguageInClient(),
					),
					"X-AREA": getCurrentAreaInClient(),
				}),
				fetch: (input, init) => {
					return fetch(input, {
						...init,
						credentials: "include",
						signal: this.abortController?.signal,
					})
				},
			}),
		)
	}

	/**
	 * Connect to SSE stream
	 */
	async connect(): Promise<void> {
		return
		// if (
		// 	this.status === ConnectionStatus.CONNECTED ||
		// 	this.status === ConnectionStatus.CONNECTING
		// ) {
		// 	return
		// }

		// this.setStatus(ConnectionStatus.CONNECTING)

		// try {
		// 	await this.cleanupConnection()

		// 	this.abortController = new AbortController()

		// 	this.iterator = await this.orpcClient.utils.sse.streamEvents()

		// 	if (!this.iterator) {
		// 		throw new Error("Failed to create SSE stream iterator")
		// 	}

		// 	this.setStatus(ConnectionStatus.CONNECTED)
		// 	this.listenToEvents()
		// } catch (error) {
		// 	console.error("SSE connection error:", error)
		// 	this.setStatus(ConnectionStatus.ERROR)
		// 	this.emit("error", error as Error)
		// }
	}

	/**
	 * Disconnect from SSE stream
	 */
	async disconnect(): Promise<void> {
		this.setStatus(ConnectionStatus.DISCONNECTED)
		await this.cleanupConnection()
	}

	/**
	 * Clean up connection resources
	 */
	private async cleanupConnection(): Promise<void> {
		this.isListening = false

		if (this.abortController) {
			this.abortController.abort()
			this.abortController = null
		}

		if (this.iterator) {
			try {
				await this.iterator.return()
			} catch (error) {
				console.error("Error closing SSE iterator:", error)
			}
			this.iterator = null
		}
	}

	/**
	 * Listen to SSE events
	 */
	private async listenToEvents(): Promise<void> {
		if (!this.iterator) return

		this.isListening = true
		this.lastEventTimestamp = Date.now()

		try {
			for await (const event of this.iterator) {
				if (
					!this.isListening ||
					this.status === ConnectionStatus.DISCONNECTED
				) {
					break
				}
				this.lastEventTimestamp = Date.now()
				const realtimeEvent: RealtimeEvent = {
					...event,
					timestamp: event.timestamp || new Date().toISOString(),
				}
				this.emit("event", realtimeEvent)
			}

			if (this.isListening && this.status === ConnectionStatus.CONNECTED) {
				this.setStatus(ConnectionStatus.DISCONNECTED)
			}
		} catch (error) {
			if (this.isListening && this.status !== ConnectionStatus.DISCONNECTED) {
				console.error("SSE stream error:", error)
				this.emit("error", error as Error)
				this.setStatus(ConnectionStatus.ERROR)
			}
		} finally {
			this.isListening = false
		}
	}

	/**
	 * Get connection status
	 */
	getStatus(): ConnectionStatus {
		return this.status
	}

	/**
	 * Get last event timestamp
	 */
	getLastEventTimestamp(): number | null {
		return this.lastEventTimestamp
	}

	/**
	 * Set connection status
	 */
	private setStatus(status: ConnectionStatus): void {
		if (this.status !== status) {
			this.status = status
			this.emit("statusChange", this.status)
		}
	}
}
